// Minimal, safe Markdown -> HTML renderer for AulaPronta content
// Also strips LaTeX math delimiters used by the AI ($...$ and $$...$$)
// while preserving currency prefixes like "R$", "US$", "$", followed by digits.

function escapeHTML(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Convert common LaTeX math snippets into plain readable math.
function latexToPlain(expr) {
  let s = expr;
  // \frac{a}{b} -> (a/b) or a/b for simple cases
  s = s.replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, (_, a, b) => `${a}/${b}`);
  // \sqrt{x} -> √(x)
  s = s.replace(/\\sqrt\s*\{([^{}]+)\}/g, (_, a) => `√(${a})`);
  // \cdot -> ·, \times -> ×, \div -> ÷, \pm -> ±, \leq/\geq -> ≤/≥, \neq -> ≠
  s = s.replace(/\\cdot/g, "·").replace(/\\times/g, "×").replace(/\\div/g, "÷");
  s = s.replace(/\\pm/g, "±").replace(/\\mp/g, "∓");
  s = s.replace(/\\leq|\\le/g, "≤").replace(/\\geq|\\ge/g, "≥").replace(/\\neq|\\ne/g, "≠");
  s = s.replace(/\\approx/g, "≈").replace(/\\infty/g, "∞");
  s = s.replace(/\\pi/g, "π").replace(/\\alpha/g, "α").replace(/\\beta/g, "β")
       .replace(/\\theta/g, "θ").replace(/\\lambda/g, "λ").replace(/\\mu/g, "μ")
       .replace(/\\Delta/g, "Δ").replace(/\\Sigma/g, "Σ");
  // ^{n} or ^n -> superscripts for common cases
  const supMap = { "0":"⁰","1":"¹","2":"²","3":"³","4":"⁴","5":"⁵","6":"⁶","7":"⁷","8":"⁸","9":"⁹","+":"⁺","-":"⁻","(":"⁽",")":"⁾","n":"ⁿ","x":"ˣ" };
  s = s.replace(/\^\{([^{}]+)\}/g, (_, g) => [...g].map(c => supMap[c] || `^${c}`).join(""));
  s = s.replace(/\^([0-9nx+\-])/g, (_, c) => supMap[c] || `^${c}`);
  // _{n} or _n -> subscripts
  const subMap = { "0":"₀","1":"₁","2":"₂","3":"₃","4":"₄","5":"₅","6":"₆","7":"₇","8":"₈","9":"₉","+":"₊","-":"₋","(":"₍",")":"₎","n":"ₙ","x":"ₓ" };
  s = s.replace(/_\{([^{}]+)\}/g, (_, g) => [...g].map(c => subMap[c] || `_${c}`).join(""));
  s = s.replace(/_([0-9nx])/g, (_, c) => subMap[c] || `_${c}`);
  // strip remaining backslash commands
  s = s.replace(/\\[a-zA-Z]+\s?/g, "");
  // strip stray braces
  s = s.replace(/[{}]/g, "");
  return s.trim();
}

// Sanitize LaTeX math delimiters while preserving R$, US$, C$, $ before digits
export function sanitizeMathDelimiters(md) {
  if (!md) return md;
  let out = md;
  // 1) Block math $$...$$
  out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_, inner) => latexToPlain(inner));
  // 2) \( ... \) and \[ ... \]
  out = out.replace(/\\\(([\s\S]+?)\\\)/g, (_, inner) => latexToPlain(inner));
  out = out.replace(/\\\[([\s\S]+?)\\\]/g, (_, inner) => latexToPlain(inner));
  // 3) Inline $...$ but skip currency:
  //    - preceded by letters (R$, US$, C$, A$) OR followed by whitespace+digit like "$ 19,90"
  //    - a lone "$digits[,.digits]" pattern (e.g., "$19,90") is currency
  // Approach: only replace $...$ when the content clearly looks like math
  // (contains letters/operators and does NOT start with a digit-only currency amount).
  out = out.replace(/(^|[^A-Za-z\\])\$([^\n$]{1,200}?)\$(?!\d)/g, (m, pre, inner) => {
    // Skip currency: "$ 19,90" or "$19.90" pure numeric
    if (/^\s*\d[\d.,\s]*$/.test(inner)) return m;
    // Skip if the char before was a letter (means it was RS$, US$, etc.)
    return pre + latexToPlain(inner);
  });
  return out;
}

export function markdownToHtml(mdRaw = "") {
  const md = sanitizeMathDelimiters(mdRaw);
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let inUl = false, inOl = false;

  const closeLists = () => {
    if (inUl) { out.push("</ul>"); inUl = false; }
    if (inOl) { out.push("</ol>"); inOl = false; }
  };

  const inline = (t) => {
    let s = escapeHTML(t);
    s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[\s(])\*(?!\s)([^*]+?)\*(?=[\s.,;:!?)]|$)/g, "$1<em>$2</em>");
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
    return s;
  };

  for (let raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { closeLists(); continue; }

    let m;
    if ((m = line.match(/^###\s+(.*)/))) { closeLists(); out.push(`<h3>${inline(m[1])}</h3>`); continue; }
    if ((m = line.match(/^##\s+(.*)/))) { closeLists(); out.push(`<h2>${inline(m[1])}</h2>`); continue; }
    if ((m = line.match(/^#\s+(.*)/))) { closeLists(); out.push(`<h1>${inline(m[1])}</h1>`); continue; }
    if (/^---+$/.test(line.trim())) { closeLists(); out.push("<hr/>"); continue; }
    if ((m = line.match(/^\s*[-*]\s+(.*)/))) {
      if (inOl) { out.push("</ol>"); inOl = false; }
      if (!inUl) { out.push("<ul>"); inUl = true; }
      out.push(`<li>${inline(m[1])}</li>`); continue;
    }
    if ((m = line.match(/^\s*\d+\.\s+(.*)/))) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (!inOl) { out.push("<ol>"); inOl = true; }
      out.push(`<li>${inline(m[1])}</li>`); continue;
    }
    closeLists();
    out.push(`<p>${inline(line)}</p>`);
  }
  closeLists();
  return out.join("\n");
}
